'use client';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';

import { SubmitHandler, useForm } from 'react-hook-form';
import { subscribeSchema } from 'shared/validation/schemas';

import { useMutation, gql } from '@apollo/client';
import { useAuth } from 'shared/misc/hooks/useAuth';
import { useRouter } from '@tanstack/react-router';
import { ModalNames, useModalState } from '../../../../apps/web2/app/misc/wrappers/modal-provider';
import { useEffect } from 'react';
import { isBefore, sub } from 'date-fns';
import SimpleSubscribeModal from './modals/simple-subscribe-modal';
import { useAnalytics } from 'shared/misc/providers/AnalyticsContext';
import { USER_SIGN_UP_STEP } from 'shared/data/user';
import { cn } from '../utils/cn';
import useUpdateSetupStep from 'shared/misc/hooks/useUpdateSetupStep';
import { usePathname } from '../utils/tan-router';
import { useFacebookPixel } from 'shared/misc/providers/FacebookPixelProvider';

type Inputs = {
  email: string;
};

type Props = {
  ctaText?: string;
  ctaSubtitle?: React.ReactNode | string;
  ctaPlaceholder?: string;
  confirmationText?: string;
  promptToInstallApp?: boolean;
  submitText?: string;
  inviteCode?: string | null;
  redirectTo?: string | null;
  autoOpenModalVersion?: boolean;
  autoOpenTimeout?: number;
  showForAuthenticatedUsers?: boolean;
  onlyInput?: boolean;
  onFinish?: () => void;
  containerProps?: {
    className?: string;
  };
  withBorder?: boolean;
  withPadding?: boolean;
  withAutoFocus?: boolean;
  eventId?: string | null;
  eventTitle?: string | null;
  enableRedirectToEmailSignIn?: boolean;
  updateSignUpStep?: boolean;
  withVerifyModal?: boolean;
  withFinishSignUpModal?: boolean;
  withSuggestSignUpModal?: boolean;
  withInputDashedBorder?: boolean;
};

const SUBSCRIBE_LAST_NOTICE_DATE = 'SUBSCRIBE_LAST_NOTICE_DATE';

export default function InlineSubscribeToSignUp({
  ctaText = `A taste of taste`,
  ctaSubtitle = 'Join the 130,000+ people using PI.FYI to get recommendations.',
  ctaPlaceholder = 'ENTER YOUR EMAIL',
  submitText = 'Submit',
  confirmationText = 'Confirmed!',
  redirectTo = '/sign-up/more',
  autoOpenModalVersion = false,
  autoOpenTimeout = 5 * 1000,
  promptToInstallApp = true,
  showForAuthenticatedUsers = false,
  onFinish,
  inviteCode,
  onlyInput,
  containerProps,
  withBorder = true,
  withPadding = true,
  withAutoFocus = false,
  eventId = null,
  withFinishSignUpModal = false,
  withVerifyModal = false,
  withSuggestSignUpModal = false,
  eventTitle = null,
  withInputDashedBorder = true,
  enableRedirectToEmailSignIn = true,
  updateSignUpStep = true,
}: Props) {
  const { signIn, ...auth } = useAuth();
  const toast = useToast();
  const finishSignUpModalState = useModalState(ModalNames.FINISH_SIGN_UP);
  const verifyModalState = useModalState(ModalNames.VERIFY);
  const suggestSignUpModalState = useModalState(ModalNames.SUGGEST_SIGN_UP);
  const simpleSubscribeModalState = useDisclosure();
  const { updateStep } = useUpdateSetupStep();

  const { trackFacebookEvent } = useFacebookPixel();

  const { handleSubmit, register, formState, watch } = useForm<Inputs>({
    resolver: yupResolver(subscribeSchema),
  });

  const { errors } = formState;

  const values = watch();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (autoOpenModalVersion && !token && !auth.user) {
      const date = localStorage.getItem(SUBSCRIBE_LAST_NOTICE_DATE);

      let hasEnoughTimePassed = false;

      if (date) {
        try {
          hasEnoughTimePassed = isBefore(new Date(date), sub(new Date(), { days: 3 }));
        } catch (e) {}
      }
      if (
        !(window as any)?.['HAS_SEEN_SIGN_UP_MODAL'] &&
        (!date || (date && hasEnoughTimePassed && !values.email))
      ) {
        const timeoutRef = setTimeout(() => {
          if (!formState.isDirty && !(window as any)?.['HAS_SEEN_SIGN_UP_MODAL']) {
            localStorage.setItem(SUBSCRIBE_LAST_NOTICE_DATE, new Date().toString());
            (window as any)['HAS_SEEN_SIGN_UP_MODAL'] = true;
            simpleSubscribeModalState.onOpen();
          }
        }, autoOpenTimeout);

        return () => clearTimeout(timeoutRef);
      }
    }
  }, [values?.email, autoOpenTimeout, autoOpenModalVersion]);

  const [mutation, mutationInfo] = useMutation(SIGN_UP_EMAIL_ONLY);
  const [markAsAttendingEvent, markAsAttendingEventInfo] = useMutation(MARK_AS_ATTENDING_EVENT);

  const router = useRouter();

  const { trackEvent } = useAnalytics();

  const pathname = usePathname();

  const onSubmit: SubmitHandler<Inputs> = async ({ email }, event) => {
    event?.preventDefault();
    await trackEvent('Click', 'Subscribe_Button', { email, inviteCode });
    await mutation({
      variables: {
        input: { email },
        inviteCode,
        shouldConfirm: true,
      },
      onCompleted: async (data) => {
        const token = data?.signUpEmailOnly;
        trackFacebookEvent('CompleteRegistration', {
          status: true,
        });
        if (token) {
          await signIn(token);
          if (
            updateSignUpStep &&
            !withFinishSignUpModal &&
            !withVerifyModal &&
            !withSuggestSignUpModal &&
            !eventId &&
            !eventTitle
          ) {
            await updateStep(USER_SIGN_UP_STEP.MORE);
          }
        }
        toast({
          title: confirmationText,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });

        if (withFinishSignUpModal) {
          finishSignUpModalState.onOpen();
        } else if (withSuggestSignUpModal) {
          suggestSignUpModalState.onOpen();
        } else if (redirectTo) {
          router.navigate({ to: redirectTo });
        } else if (eventId || eventTitle) {
          await markAsAttendingEvent({
            variables: { eventId, eventTitle },
          });
        }
      },
      onError: (e) => {
        // console.log('error', e.graphQLErrors[0]?.extensions?.surfaceToUser);
        if (e.graphQLErrors[0]?.extensions?.surfaceToUser) {
          const { field, redirectToEmailSignIn } = e.graphQLErrors[0].extensions;

          if (redirectToEmailSignIn && enableRedirectToEmailSignIn) {
            toast({
              title: 'Check your email for a code!',
              status: 'success',
              duration: 3000,
              isClosable: true,
              position: 'top',
            });
            if (withVerifyModal) {
              verifyModalState.onOpen();
              return;
            }
            if (pathname === '/welcome') {
              router.navigate({
                to: `/sign-in?context=code-sign-in&email=${encodeURIComponent(values.email)}`,
              });
            } else {
              router.navigate({
                to: `/sign-in?context=code-sign-in&email=${encodeURIComponent(
                  values.email,
                )}&destination=${encodeURIComponent(pathname)}`,
              });
            }
          }

          if (field === 'email') {
            // console.log('error with email', e.message);
            return;
          }
        }
      },
    });
  };

  const onMarkAsAttendingEvent = async () => {
    await markAsAttendingEvent({
      variables: { eventId, eventTitle },
    });
  };

  const containerClassName = cn(
    withBorder ? (onlyInput ? undefined : 'border-main border-[1px] border-dashed') : undefined,
    withPadding ? (onlyInput ? undefined : 'p-[12px] md:p-[24px]') : undefined,
    'p-12px p-24px',
    // 'bg-brand-background',
    containerProps?.className,
  );

  if (auth.user && (eventId || eventTitle)) {
    return (
      <div
        className={cn(containerClassName, 'flex flex-col gap-[12px]', containerProps?.className)}
      >
        <div className="flex flex-col justify-center gap-[18px] md:gap-[24px]">
          {markAsAttendingEventInfo.data ? (
            <>
              <div className="typography-heading-lg text-brand-highlight text-xl md:text-2xl self-center text-center">
                Thanks for letting us know.
              </div>
              <div className="flex self-center flex-row gap-[12px]">
                <button className="btn-secondary" onClick={() => router.navigate({ to: '/' })}>
                  Go home
                </button>
                <button
                  className="btn-primary"
                  onClick={() => router.navigate({ to: '/sign-up/more' })}
                >
                  Complete your profile
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="typography-heading-lg text-brand-highlight text-xl md:text-2xl self-center text-center">
                {ctaText}
              </div>
              {ctaSubtitle && (
                <div className="typography-body-sm text-brand-lightgrey text-center">
                  {ctaSubtitle}
                </div>
              )}
              <button
                className={`btn-primary w-full ${
                  submitText.length < 5 ? 'text-xl md:text-xl' : 'text-base md:text-xl'
                } disabled:opacity-50`}
                onClick={async () => {
                  await onMarkAsAttendingEvent();
                  toast({
                    title: confirmationText,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                  });
                }}
                disabled={markAsAttendingEventInfo.loading}
              >
                {markAsAttendingEventInfo.loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-main border-t-transparent" />
                    Loading...
                  </span>
                ) : (
                  submitText
                )}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Signed in from the subscribe flow.
  if (auth.user && mutationInfo.data?.signUpEmailOnly) {
    return (
      <div
        className={cn(
          'flex flex-col gap-[12px]',
          withBorder && !onlyInput && 'border border-dashed border-brand-main',
          withPadding && !onlyInput && 'p-[18px] md:p-[24px]',
          containerProps?.className,
        )}
        {...containerProps}
      >
        <div className="typography-heading-lg text-brand-highlight text-2xl self-center">
          {confirmationText}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(containerClassName, containerProps?.className)}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[18px] md:gap-[24px]">
        {onlyInput ? null : (
          <div className="flex flex-col gap-[12px]">
            <p className="text-brand-highlight typography-heading-lg text-[20px] md:text-[24px] text-center self-center">
              {ctaText}
            </p>
            {ctaSubtitle && (
              <div
                className="typography-heading-lg text-[16px] md:text-[20px] text-center self-center"
                style={{
                  color: 'var(--theme-main)',
                }}
              >
                {ctaSubtitle}
              </div>
            )}
          </div>
        )}
        <div className={cn('self-center', 'w-full')}>
          <div className="relative flex w-full">
            <input
              placeholder={ctaPlaceholder || 'ENTER YOUR EMAIL'}
              className={cn(
                'w-full uppercase bg-brand-background border border-main p-[12px] typography-action-sm md:typography-heading-md',
                'font-bold font-arial-narrow-bold border-r-0 placeholder:typography-action-sm md:placeholder:typography-heading-md placeholder:font-arial-narrow-bold',
                'placeholder:font-bold focus:border focus:border-brand-main focus:border-r-0 focus:outline-none focus:ring-0',
                withInputDashedBorder ? 'border-dashed' : 'border',
              )}
              autoFocus={withAutoFocus}
              {...register('email')}
            />
            <button
              type="submit"
              disabled={mutationInfo.loading}
              className={`px-2 py-1 flex items-center justify-center bg-brand-highlight text-brand-background p-1 ${
                submitText.length < 6
                  ? 'typography-action-sm md:typography-heading-sm w-[64px] md:w-[100px]'
                  : 'typography-action-sm md:typography-heading-sm w-[90px] md:w-[140px]'
              } btn-primary disabled:opacity-50 `}
            >
              {submitText}
              {mutationInfo.loading ? '...' : ''}
            </button>
          </div>
          {errors.email && <p className="text-red">{errors.email?.message}</p>}
        </div>
      </form>
      {simpleSubscribeModalState.isOpen && (
        <SimpleSubscribeModal redirectTo={redirectTo} modalState={simpleSubscribeModalState} />
      )}
    </div>
  );
}

const SIGN_UP_EMAIL_ONLY = gql(/* GraphQL */ `
  mutation signUpEmailOnly(
    $input: SignUpEmailOnlyInput!
    $inviteCode: String
    $shouldConfirm: Boolean
    $requireConfirm: Boolean
  ) {
    signUpEmailOnly(
      input: $input
      inviteCode: $inviteCode
      shouldConfirm: $shouldConfirm
      requireConfirm: $requireConfirm
    )
  }
`);

const MARK_AS_ATTENDING_EVENT = gql(/* GraphQL */ `
  mutation markAsAttendingEvent(
    $eventId: String
    $eventTitle: String
    $status: EventAttendeeStatus
  ) {
    markAsAttendingEvent(eventId: $eventId, eventTitle: $eventTitle, status: $status)
  }
`);
